import { useMemo } from "react";
import { useCurrentUser } from "./useCurrentUser";
import { useGetDistrictById } from "./useGetDistrictById";
import {
  formatWindowStart,
  isCompleteWindow,
  isWithinAnyWindow,
  nextWindowStart,
} from "@/utils/date-window";

export interface PlanAvailability {
  /** Planning is open right now. */
  isAvailable: boolean;
  /** The district has configured at least one window. */
  isConfigured: boolean;
  /** "March 1" when a window is configured but closed; null otherwise. */
  opensOn: string | null;
  /** The district record is still in flight; nothing below is decided yet. */
  isLoading: boolean;
  /** The district record could not be read, so availability is unknown. */
  isError: boolean;
}

/**
 * When a district's climb plans and course plans are open, from the month/day
 * windows on its district record.
 *
 * A district with no windows has planning closed — districts opt in. This is a
 * UX gate, not a permission: the server does not enforce it.
 */
export const usePlanAvailability = (): PlanAvailability => {
  const currentUser = useCurrentUser();
  const districtId = currentUser?.districtId;

  const { data: district, isError } = useGetDistrictById(districtId);

  // Not react-query's `isLoading`: that is `isPending && isFetching`, which is
  // false both when the query is disabled (no districtId on the user path) and
  // after it errors. Either would otherwise read as a settled "no windows", and
  // a climber whose district is open would be bounced off the Planner with
  // nothing to distinguish it from a district that never opted in.
  const isLoading = !!districtId && !district && !isError;

  return useMemo(() => {
    // Only windows with all four edges count. A partial one cannot be
    // evaluated, so counting it as configured would show the entry point with
    // no date to put on it — "Available null".
    const windows = (district?.planAvailability ?? []).filter(isCompleteWindow);
    const isConfigured = windows.length > 0;
    const isAvailable = isWithinAnyWindow(windows);
    const start =
      isConfigured && !isAvailable ? nextWindowStart(windows) : null;

    return {
      isAvailable,
      isConfigured,
      opensOn: start ? formatWindowStart(start) : null,
      isLoading,
      isError,
    };
  }, [district, isLoading, isError]);
};
